// v4 (spec-v4-structure-and-stage.md 5.2, decision E): pure helpers behind components/Fold.tsx. A fold remembers
// whether it's open per fold id in localStorage; storage can be missing or throw (private mode, previews), so every
// read and write is guarded and the fold falls back to its default.

/** The smallest Storage the fold needs (window.localStorage, or a fake in tests). */
export type FoldStore = Pick<Storage, "getItem" | "setItem">;

const PREFIX = "cao.fold.";
export const foldKey = (id: string) => `${PREFIX}${id}`;

function store(s?: FoldStore | null): FoldStore | null {
  if (s !== undefined) return s;
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** Whether fold `id` was left open; `fallback` when nothing is stored (or storage isn't there). */
export function readFold(id: string, fallback: boolean, s?: FoldStore | null): boolean {
  try {
    const v = store(s)?.getItem(foldKey(id));
    return v === "1" ? true : v === "0" ? false : fallback;
  } catch {
    return fallback;
  }
}

/** Remembers fold `id`'s state; never throws. */
export function writeFold(id: string, open: boolean, s?: FoldStore | null): void {
  try {
    store(s)?.setItem(foldKey(id), open ? "1" : "0");
  } catch {
    // storage full or blocked: the fold still works, it just won't remember
  }
}

/** The one-line summary: the parts that have something, joined with " · " ("Kling 2.5 · 85"). */
export function foldSummary(parts: (string | number | null | undefined | false)[]): string {
  return parts
    .filter((x): x is string | number => x !== null && x !== undefined && x !== false && String(x).trim() !== "")
    .map((x) => String(x).trim())
    .join(" · ");
}

/** A DOM-safe id for the fold's body (aria-controls). */
export const foldBodyId = (id: string) => `fold-${id.replace(/[^A-Za-z0-9_-]/g, "-")}`;

// v4 (V4-D4 show_references, V4-D5 fold): the Director opens or closes a fold by id. The state is remembered (so a fold
// mounted later opens too) and folds on screen hear it at once.
export const FOLD_EVENT = "cao:fold";
export type FoldEventDetail = { id: string; open: boolean };

/** Open or close fold `id` from outside it (a Director directive). */
export function setFold(id: string, open: boolean, s?: FoldStore | null): void {
  writeFold(id, open, s);
  try {
    if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent<FoldEventDetail>(FOLD_EVENT, { detail: { id, open } }));
  } catch {
    // no window (tests): the stored state is enough
  }
}

/**
 * v4 (V4-D5 `fold` tool): the Director names a section ("versions", "settings"); the fold with that name on the
 * current screen is the one meant. An id with a dot ("scene.references") is already exact. `mounted` lists the fold
 * ids on screen in document order (Fold.tsx renders data-fold); the first whose last part (or first part) is the panel
 * wins. `stage` lists those inside the Director's stage: they come first while the stage is showing (`onStage`), last
 * when it's tucked away. Nothing matches: the name as given (a fold mounted later with that exact id still opens).
 */
export function resolveFoldId(id: string, mounted: readonly string[], stage: readonly string[] = [], onStage = false): string {
  if (id.includes(".")) return id;
  const want = id.toLowerCase();
  const hit = (ids: readonly string[]) => ids.find((m) => { const l = m.toLowerCase(); return l === want || l.split(".").at(-1) === want || l.startsWith(`${want}.`); });
  const outside = mounted.filter((m) => !stage.includes(m));
  return (onStage ? hit(stage) ?? hit(outside) : hit(outside) ?? hit(stage)) ?? id;
}

/** The fold ids on screen now (document order) and those inside the stage. Empty without a DOM. */
export function mountedFolds(): { all: string[]; stage: string[] } {
  if (typeof document === "undefined") return { all: [], stage: [] };
  const ids = (sel: string) => [...document.querySelectorAll<HTMLElement>(sel)].map((e) => e.dataset.fold ?? "").filter(Boolean);
  return { all: ids("[data-fold]"), stage: ids(".stg [data-fold]") };
}
